import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  OrderListParams,
  OrderListResponse,
  PaymentStatusFilter,
} from '../types/orderTypes';

const DEFAULT_LIMIT = 30;

export const useOrderList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const currentPage = useMemo(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  }, [searchParams]);

  const searchKeyword = useMemo(() => {
    return searchParams.get('keyword') || '';
  }, [searchParams]);

  const paymentStatusFilter = useMemo(() => {
    const status = searchParams.get('statusFilter');
    if (status === 'PENDING' || status === 'CANCELLED' || status === 'PAID') {
      return status as PaymentStatusFilter;
    }
    return 'ALL';
  }, [searchParams]);

  const queryParams = useMemo<OrderListParams>(
    () => ({
      page: currentPage,
      limit: DEFAULT_LIMIT,
      keyword: searchKeyword,
      statusFilter: paymentStatusFilter,
    }),
    [currentPage, searchKeyword, paymentStatusFilter],
  );

  const getOrderList = useCallback(
    async (
      params: OrderListParams,
    ): Promise<ApiResponse<OrderListResponse>> => {
      const urlSearchParams = new URLSearchParams({
        page: params.page.toString(),
        limit: params.limit.toString(),
      });

      if (params.keyword && params.keyword.trim()) {
        urlSearchParams.append('keyword', params.keyword);
      }

      if (params.statusFilter && params.statusFilter !== 'ALL') {
        urlSearchParams.append('statusFilter', params.statusFilter);
      }

      if (
        params.deliveryMethodFilter &&
        params.deliveryMethodFilter !== 'ALL'
      ) {
        urlSearchParams.append(
          'deliveryMethodFilter',
          params.deliveryMethodFilter,
        );
      }

      return api.get<OrderListResponse>(
        `/api/v1/order/list?${urlSearchParams.toString()}`,
      );
    },
    [],
  );

  const {
    data: orderListResponse,
    isLoading,
    error,
    refetch,
  } = useApiQuery(['orderList', JSON.stringify(queryParams)], () =>
    getOrderList(queryParams),
  );

  const orders = orderListResponse?.data?.orderList || [];
  const totalCount = orderListResponse?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_LIMIT);

  const updateURLParams = useCallback(
    (updates: {
      page?: number;
      keyword?: string;
      statusFilter?: PaymentStatusFilter;
    }) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (updates.page !== undefined) {
        newSearchParams.set('page', updates.page.toString());
      }

      if (updates.keyword !== undefined) {
        if (updates.keyword.trim()) {
          newSearchParams.set('keyword', updates.keyword);
        } else {
          newSearchParams.delete('keyword');
        }
      }

      if (updates.statusFilter !== undefined) {
        if (updates.statusFilter !== 'ALL') {
          newSearchParams.set('statusFilter', updates.statusFilter);
        } else {
          newSearchParams.delete('statusFilter');
        }
      }

      navigate(`/orders?${newSearchParams.toString()}`, { replace: true });
    },
    [navigate, searchParams],
  );

  const handleSearch = useCallback(
    (keyword: string) => {
      updateURLParams({
        page: 1,
        keyword,
      });
    },
    [updateURLParams],
  );

  const handlePaymentStatusChange = useCallback(
    (status: PaymentStatusFilter) => {
      updateURLParams({
        page: 1,
        statusFilter: status,
      });
    },
    [updateURLParams],
  );

  const setCurrentPage = useCallback(
    (page: number) => {
      updateURLParams({
        page,
      });
    },
    [updateURLParams],
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const resetFilters = useCallback(() => {
    navigate('/orders?page=1', { replace: true });
  }, [navigate]);

  return {
    // Data
    orders,
    totalCount,
    totalPages,
    isLoading,
    error,

    // Filters
    currentPage,
    searchKeyword,
    paymentStatusFilter,

    // Actions
    setCurrentPage,
    handleSearch,
    handlePaymentStatusChange,
    handleRefresh,
    resetFilters,
  };
};
